import * as THREE from 'three'
import type {
  SpatialRepresentationDebugSnapshot,
  SpatialRepresentationMap,
  SpatialRepresentationRole,
  SpatialRepresentationSnapshot,
  SpatialRepresentationState,
  SpatialSourceFormat,
  SpatialWorldHandle,
  SpatialWorldSnapshot,
} from '@/shared/types/spatial'
import { generateId } from '@/shared/utils'
import type {
  SpatialCollisionBackend,
  SpatialNavigationBackend,
  SpatialRepresentationBuildResult,
  SpatialSurfaceBackend,
} from './spatialBackend'

type SpatialBackend = SpatialSurfaceBackend | SpatialCollisionBackend | SpatialNavigationBackend

/** world 创建参数 */
export interface SpatialWorldCreateOptions {
  /** 显式指定 world ID；省略自动生成 */
  id?: string
  /** 显示名称，默认等于 id */
  name?: string
  /** 业务自定义数据 */
  userData?: Record<string, unknown>
  /** 各角色表达的初始状态（格式/调试/用户数据） */
  representations: Partial<
    Record<
      SpatialRepresentationRole,
      {
        format: SpatialSourceFormat | null
        debug: SpatialRepresentationDebugSnapshot
        userData?: Record<string, unknown>
      }
    >
  >
}

/** 空间表达内部记录，在快照基础上持有后端与调试节点引用 */
export interface SpatialRepresentationRecord extends SpatialRepresentationSnapshot {
  /** 后端实例；未就绪或失败时为 null */
  backendInstance: SpatialBackend | null
  /** 调试节点原型；未构建或不可见时为 null */
  debugPrototype: THREE.Object3D | null
  /** 调试节点版本号，每次重建递增 */
  debugRevision: number
}

/** world 内部记录，持有表达映射与对外 handle */
export interface SpatialWorldRecord extends SpatialWorldSnapshot {
  /** world 代际号，用于隔离同 ID 卸载重建后的异步构建回写 */
  generation: number
  /** 各角色表达记录映射 */
  representations: SpatialRepresentationMap<SpatialRepresentationRecord>
  /** 对外 world 句柄实例 */
  handle: SpatialWorldHandleImpl
}

/**
 * SpatialWorld 仓库，负责 world 记录的创建、更新、引用计数与快照。
 */
export class SpatialWorldStore {
  private readonly records = new Map<string, SpatialWorldRecord>()
  private nextGeneration = 1

  constructor(readonly unloadWorld: (worldId: string) => boolean) {}

  /**
   * 创建 world 记录并分配句柄。
   *
   * @param options - 创建参数
   * @returns world 句柄
   */
  create(options: SpatialWorldCreateOptions): SpatialWorldHandle {
    const id = options.id ?? generateId()
    if (this.records.has(id)) {
      throw new Error(`Spatial world "${id}" already exists`)
    }

    const record: SpatialWorldRecord = {
      id,
      name: options.name?.trim() || id,
      generation: this.nextGeneration++,
      refCount: 0,
      userData: Object.freeze({ ...(options.userData ?? {}) }),
      representations: createRepresentationRecordMap(id, options.representations),
      handle: new SpatialWorldHandleImpl(id, this),
    }

    this.records.set(id, record)
    return record.handle
  }

  /**
   * 获取 world 内部记录。
   *
   * @param id - world ID
   * @returns world 记录；未找到返回 undefined
   */
  getRecord(id: string): SpatialWorldRecord | undefined {
    return this.records.get(id)
  }

  /**
   * 获取指定角色表达记录。
   *
   * @param id - world ID
   * @param role - 表达角色
   * @returns 表达记录；未找到返回 undefined
   */
  getRepresentationRecord(
    id: string,
    role: SpatialRepresentationRole
  ): SpatialRepresentationRecord | undefined {
    return this.records.get(id)?.representations[role]
  }

  /**
   * 获取 world 对外句柄。
   *
   * @param id - world ID
   * @returns world 句柄；未找到返回 undefined
   */
  getHandle(id: string): SpatialWorldHandle | undefined {
    return this.records.get(id)?.handle
  }

  /**
   * 查询表达状态。
   *
   * @param id - world ID
   * @param role - 表达角色
   * @returns 状态；未找到返回 'absent'
   */
  getRepresentationState(id: string, role: SpatialRepresentationRole): SpatialRepresentationState {
    return this.records.get(id)?.representations[role]?.state ?? 'absent'
  }

  /**
   * 获取 world 快照。
   *
   * @param id - world ID
   * @returns 快照；未找到返回 undefined
   */
  getSnapshot(id: string): SpatialWorldSnapshot | undefined {
    const record = this.records.get(id)
    return record ? createSnapshot(record) : undefined
  }

  /**
   * 列出全部 world 快照。
   *
   * @returns 快照数组
   */
  listSnapshots(): SpatialWorldSnapshot[] {
    return Array.from(this.records.values(), createSnapshot)
  }

  /**
   * 将表达标记为就绪并挂载后端与调试节点。
   *
   * @param id - world ID
   * @param role - 表达角色
   * @param result - 构建结果
   * @returns 更新后的表达记录；未找到返回 undefined
   */
  markRepresentationReady(
    id: string,
    role: SpatialRepresentationRole,
    result: SpatialRepresentationBuildResult<SpatialBackend>,
    generation?: number
  ): SpatialRepresentationRecord | undefined {
    const world = this.records.get(id)
    if (!world || (generation !== undefined && world.generation !== generation)) {
      return undefined
    }

    const record = world.representations[role]
    if (!record) {
      return undefined
    }

    disposeBackend(record.backendInstance)
    record.state = 'ready'
    record.format = result.format
    record.backend = result.backend.kind
    record.backendInstance = result.backend
    record.bounds = result.bounds?.clone() ?? null
    record.error = null
    record.userData = result.userData
    record.metadata = result.metadata
    disposeDebugObject(record.debugPrototype)
    record.debug = {
      ...result.debug,
      prototypeName: `${id}-${role}-${result.debug.prototypeName ?? 'prototype'}`,
    }
    record.debugPrototype = record.debug
      ? result.backend.createDebugPrototype(
          record.debug.prototypeName ?? `${id}-${role}`,
          record.debug
        )
      : null
    record.debugRevision += 1
    return record
  }

  /**
   * 将表达标记为错误状态。
   *
   * @param id - world ID
   * @param role - 表达角色
   * @param error - 错误对象
   * @returns 更新后的表达记录；未找到返回 undefined
   */
  markRepresentationError(
    id: string,
    role: SpatialRepresentationRole,
    error: Error,
    generation?: number
  ): SpatialRepresentationRecord | undefined {
    const world = this.records.get(id)
    if (!world || (generation !== undefined && world.generation !== generation)) {
      return undefined
    }

    const record = world.representations[role]
    if (!record) {
      return undefined
    }

    record.state = 'error'
    record.error = error
    record.backend = null
    disposeBackend(record.backendInstance)
    record.backendInstance = null
    record.bounds = null
    record.metadata = null
    disposeDebugObject(record.debugPrototype)
    record.debugPrototype = null
    record.debugRevision += 1
    return record
  }

  /**
   * 更新表达的调试参数并重建调试节点。
   *
   * @param id - world ID
   * @param role - 表达角色
   * @param update - 调试参数增量
   * @returns 更新后的表达记录；未找到或未配置调试返回 undefined
   */
  setRepresentationDebug(
    id: string,
    role: SpatialRepresentationRole,
    update: Partial<
      Pick<SpatialRepresentationDebugSnapshot, 'meshVisible' | 'boundsVisible' | 'boundsDepth'>
    >
  ): SpatialRepresentationRecord | undefined {
    const record = this.records.get(id)?.representations[role]
    if (!record?.debug) {
      return undefined
    }

    record.debug = {
      ...record.debug,
      ...update,
    }

    disposeDebugObject(record.debugPrototype)
    record.debugPrototype =
      record.backendInstance && record.state === 'ready'
        ? record.backendInstance.createDebugPrototype(
            record.debug.prototypeName ?? `${id}-${role}`,
            record.debug
          )
        : null
    record.debugRevision += 1
    return record
  }

  /**
   * 增加 world 引用计数。
   *
   * @param id - world ID
   * @returns 更新后的记录；未找到返回 undefined
   */
  incrementRef(id: string): SpatialWorldRecord | undefined {
    const record = this.records.get(id)
    if (!record) {
      return undefined
    }

    record.refCount += 1
    return record
  }

  /**
   * 减少 world 引用计数，计数最小为 0。
   *
   * @param id - world ID
   * @returns 更新后的记录；未找到返回 undefined
   */
  decrementRef(id: string): SpatialWorldRecord | undefined {
    const record = this.records.get(id)
    if (!record) {
      return undefined
    }

    record.refCount = Math.max(0, record.refCount - 1)
    return record
  }

  /**
   * 按 ID 移除 world。
   *
   * @param id - world ID
   * @returns 被移除的记录；未找到返回 undefined
   */
  remove(id: string): SpatialWorldRecord | undefined {
    const record = this.records.get(id)
    if (!record) {
      return undefined
    }

    this.records.delete(id)
    return record
  }

  /**
   * 清空全部 world。
   *
   * @returns 清空前的全部记录，便于外部释放资源
   */
  clear(): SpatialWorldRecord[] {
    const records = Array.from(this.records.values())
    this.records.clear()
    return records
  }
}

class SpatialWorldHandleImpl implements SpatialWorldHandle {
  constructor(
    public readonly id: string,
    private readonly store: SpatialWorldStore
  ) {}

  get name(): string {
    return this.requireRecord().name
  }

  get refCount(): number {
    return this.requireRecord().refCount
  }

  get userData(): Readonly<Record<string, unknown>> {
    return this.requireRecord().userData
  }

  snapshot(): SpatialWorldSnapshot {
    return createSnapshot(this.requireRecord())
  }

  getRepresentation(role: SpatialRepresentationRole): SpatialRepresentationSnapshot {
    return cloneRepresentation(this.requireRecord().representations[role])
  }

  dispose(): void {
    this.store.unloadWorld(this.id)
  }

  private requireRecord(): SpatialWorldRecord {
    const record = this.store.getRecord(this.id)
    if (!record) {
      throw new Error(`Spatial world "${this.id}" has been released`)
    }
    return record
  }
}

function createRepresentationRecordMap(
  worldId: string,
  requested: SpatialWorldCreateOptions['representations']
): SpatialRepresentationMap<SpatialRepresentationRecord> {
  return {
    surface: createRepresentationRecord(worldId, 'surface', requested.surface),
    collision: createRepresentationRecord(worldId, 'collision', requested.collision),
    navigation: createRepresentationRecord(worldId, 'navigation', requested.navigation),
  }
}

function createRepresentationRecord(
  _worldId: string,
  role: SpatialRepresentationRole,
  requested?: {
    format: SpatialSourceFormat | null
    debug: SpatialRepresentationDebugSnapshot
    userData?: Record<string, unknown>
  }
): SpatialRepresentationRecord {
  return {
    role,
    state: requested ? 'building' : 'absent',
    format: requested?.format ?? null,
    backend: null,
    backendInstance: null,
    bounds: null,
    error: null,
    userData: Object.freeze({ ...(requested?.userData ?? {}) }),
    metadata: null,
    debug: requested ? { ...requested.debug } : null,
    debugPrototype: null,
    debugRevision: 0,
  }
}

function createSnapshot(record: SpatialWorldRecord): SpatialWorldSnapshot {
  return {
    id: record.id,
    name: record.name,
    refCount: record.refCount,
    userData: record.userData,
    representations: {
      surface: cloneRepresentation(record.representations.surface),
      collision: cloneRepresentation(record.representations.collision),
      navigation: cloneRepresentation(record.representations.navigation),
    },
  }
}

function cloneRepresentation(record: SpatialRepresentationSnapshot): SpatialRepresentationSnapshot {
  return {
    role: record.role,
    state: record.state,
    format: record.format,
    backend: record.backend,
    bounds: record.bounds?.clone() ?? null,
    error: record.error,
    userData: record.userData,
    metadata: record.metadata,
    debug: record.debug ? { ...record.debug } : null,
  }
}

function disposeDebugObject(object3D: THREE.Object3D | null | undefined): void {
  if (!object3D) {
    return
  }

  object3D.traverse((child) => {
    const disposable = child as unknown as { dispose?: () => void }
    if (
      disposable.dispose &&
      !(child instanceof THREE.Mesh) &&
      !(child instanceof THREE.LineSegments)
    ) {
      disposable.dispose()
    }

    if (child instanceof THREE.Mesh || child instanceof THREE.LineSegments) {
      const material = child.material
      if (Array.isArray(material)) {
        for (const entry of material) {
          entry.dispose()
        }
      } else {
        material?.dispose()
      }
    }
  })

  object3D.removeFromParent()
}

function disposeBackend(backend: SpatialBackend | null | undefined): void {
  backend?.dispose()
}
